import React from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import { getMediumAssetPath } from '@aytm/lib/common/assets.ts'
import { IEcommerceQuestionViewProps } from '@aytm/app/swidget/base/questions/Ecommerce/types.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import style from './Header.module.scss'

interface IHeaderProps {
  schema: IEcommerceQuestionViewProps['schema']
  findability: boolean
  totals: number[]
  onMainPage: boolean
  onCartOpen: () => void
  onHomeClick: () => void
  clearSelection: () => void
}

const Header = ({ schema, findability, totals, onMainPage, onCartOpen, onHomeClick, clearSelection }: IHeaderProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()
  return (
    <>
      {viewport.mobile ? (
        <>
          <header className={style.headerMobile}>
            <button className={style.backIcon} onClick={onHomeClick} />
            {schema.image && (
              <div
                className={style.logo}
                role="img"
                aria-label={schema.image?.alt_text || t('questions', 'img', 'noAlt')}
                style={{ backgroundImage: `url('${getMediumAssetPath(schema.image)}')` }}
              />
            )}
            <div
              className={style.condition}
              dangerouslySetInnerHTML={{ __html: `${schema.search_condition || ''}` }}
              onClick={clearSelection}
            />
            <EmptyLink color="#111">
              <div className={style.filterIcon} />
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.searchIcon} />
            </EmptyLink>
          </header>
          {!onMainPage && (
            <div className={style.headerFilters}>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'saleOffers')}</div>
              </EmptyLink>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'shopByPrice')}</div>
              </EmptyLink>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'bestSellers')}</div>
              </EmptyLink>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'customerRating')}</div>
              </EmptyLink>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'cashbackDeals')}</div>
              </EmptyLink>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'fastestDelivery')}</div>
              </EmptyLink>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'discounted')}</div>
              </EmptyLink>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'freeShipping')}</div>
              </EmptyLink>
            </div>
          )}
        </>
      ) : (
        <header className={style.header}>
          <div className={style.top}>
            <EmptyLink color="#111">
              <div className={style.topText}>{t('questions', 'ec', 'ecommerce', 'findStore')}</div>
            </EmptyLink>
            <div className={style.line}>|</div>
            <EmptyLink color="#111">
              <div className={style.topText}>{t('questions', 'ec', 'ecommerce', 'help')}</div>
            </EmptyLink>
            <div className={style.line}>|</div>
            <EmptyLink color="#111">
              <div className={style.topText}>{t('questions', 'ec', 'ecommerce', 'joinUs')}</div>
            </EmptyLink>
            <div className={style.line}>|</div>
            <EmptyLink color="#111">
              <div className={style.topText}>{t('questions', 'ec', 'ecommerce', 'signIn')}</div>
            </EmptyLink>
          </div>
          <div className={style.middle}>
            <div className={style.logoContainer}>
              {schema.image && (
                <div
                  className={style.logo}
                  style={{ backgroundImage: `url('${getMediumAssetPath(schema.image)}')` }}
                  onClick={onHomeClick}
                />
              )}
            </div>
            <div className={style.filters}>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'new')}</div>
              </EmptyLink>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'sale')}</div>
              </EmptyLink>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'freeShipping')}</div>
              </EmptyLink>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'bestSellers')}</div>
              </EmptyLink>
              <EmptyLink color="#111">
                <div className={style.filterItem}>{t('questions', 'ec', 'ecommerce', 'highlyRated')}</div>
              </EmptyLink>
            </div>
            <div className={style.search}>
              <div className={style.input}>
                <EmptyLink color="#111">
                  <div className={style.searchIcon} />
                </EmptyLink>
                <div
                  className={style.condition}
                  dangerouslySetInnerHTML={{ __html: `${schema.search_condition || ''}` }}
                  onClick={clearSelection}
                />
              </div>
              <EmptyLink color="#111">
                <div className={style.headerButton}>
                  <div className={style.heartIcon} />
                </div>
              </EmptyLink>
              {findability ? (
                <EmptyLink color="#111">
                  <div className={style.headerButton}>
                    <div className={style.cartIcon} />
                  </div>
                </EmptyLink>
              ) : (
                <div className={style.cart} onClick={onCartOpen}>
                  <div className={style.amount}>{totals[0] > 99 ? '99+' : totals[0]}</div>
                  <div className={style.cartIcon} />
                </div>
              )}
            </div>
          </div>
          {onMainPage && (
            <div className={style.backWrapper}>
              <button className={style.back} onClick={onHomeClick}>
                <i className="pi pi-chevron-left" />
                <span>{t('questions', 'ec', 'ecommerce', 'back')}</span>
              </button>
            </div>
          )}
        </header>
      )}
    </>
  )
}
export default Header
